import { FC } from "react";
import { useSelector } from "react-redux";

import { PlanFeature } from "../../constants/plans";
import { isPlanFeatureEnabled } from "@/utils/isPlanFeatureEnabled";
import { buildBrandsWithChartOpenRoute } from "../Brands/utils/brandsNavigation";
import {
  selectBrandState,
  selectSelectedBrandState,
  selectViewBrandIdState,
} from "../../store/selectors/brands";
import TopNav from "../../components/TopNav/TopNav";
import OverviewHeader from "../../components/OverviewHeader/OverviewHeader";
import OverviewStats from "../../components/OverviewStats/OverviewStats";
import Section from "../../components/Section/Section";
import OverviewScatterChart from "../../components/OverviewScatterChart";
import { OverviewRankingList } from "../../components/OverviewRankingList";
import OverviewSourceUsage from "../../components/OverviewSourceUsage/OverviewSourceUsage";
import {
  MOCK_CHART_DATA,
  MOCK_CHART_SERIES,
  MOCK_TABLE_ROWS,
} from "../../components/OverviewSourceUsage/const";
import { PlanFeatureBlurGate } from "../../components/PlanFeatureBlurGate";
import { TrophyIcon, VisibilitySentimentIcon, SourceUsageIcon } from "../../components/icons";
import { selectAuthSubscriptionPlan } from "../../store/selectors/auth";
import { useOverviewDateFilterFromUrl } from "./hooks/useOverviewDateFilterFromUrl";
import { useOverviewDataFetch } from "./hooks/useOverviewDataFetch";
import { useOverviewPageData } from "./hooks/useOverviewPageData";

import styles from "./Overview.module.css";

const MOCK_DATA = {
  rows: MOCK_TABLE_ROWS,
  chartData: MOCK_CHART_DATA,
  chartSeries: MOCK_CHART_SERIES,
};

const Overview: FC = () => {
  useOverviewDataFetch();
  const { dateFilter, setDateFilterFromUrl } = useOverviewDateFilterFromUrl();
  const pageData = useOverviewPageData();
  const subscriptionPlan = useSelector(selectAuthSubscriptionPlan);
  const selectedBrand = useSelector(selectSelectedBrandState);
  const viewBrandId = useSelector(selectViewBrandIdState);
  const mainBrand = useSelector(selectBrandState);
  const overviewBrandId =
    selectedBrand?.id ?? viewBrandId ?? mainBrand?.id ?? null;
  const linkToBrands =
    overviewBrandId != null
      ? buildBrandsWithChartOpenRoute(overviewBrandId)
      : undefined;
  const isSourceLocked = 
    subscriptionPlan != null && 
    !isPlanFeatureEnabled({feature: PlanFeature.Source, plan: subscriptionPlan});
  const dataSourceUsageData = pageData.domainsRowsTop.length ? {
    rows: pageData.domainsRowsTop,
    chartData: pageData.chartData,
    chartSeries: pageData.chartSeries,
  } : MOCK_DATA;

  return (
    <>
      <TopNav title="Overview" />
      <div className={styles["overview"]}>
        <div className={styles["overview__grid"]}>
          <div className={styles["overview__row"]}>
            <OverviewHeader
              lastUpdateTime={pageData.lastUpdateTime}
              dateFilter={dateFilter}
              onDateFilterChange={setDateFilterFromUrl}
            />
          </div>

          <div className={styles["overview__row"]}>
            <Section>
              <OverviewStats
                visibility={pageData.visibility}
                sentiment={pageData.sentiment}
                ranking={pageData.ranking}
                isLoading={pageData.isLoading}
                topModel={pageData.topModel}
                topModelLoading={pageData.topModelLoading}
                topModelLoadComplete={pageData.topModelLoadComplete}
                onTopModelClick={pageData.onTopModelClick}
                topSource={pageData.topSource}
                topSourceLoading={pageData.showSourceUsageSkeleton}
                linkToBrands={linkToBrands}
              />
            </Section>
          </div>

          <div className={`${styles["overview__row"]} ${styles["is-scatter-row"]}`}>
            <Section
              infoTooltip="Visibility & Sentiment"
              icon={<VisibilitySentimentIcon />}
              title="Visibility & Sentiment"
            >
              <OverviewScatterChart
                scatterChartData={pageData.scatterChartData}
                isLoading={pageData.isLoading}
              />
            </Section>
            <Section infoTooltip="Ranking" icon={<TrophyIcon />} title="Ranking">
              <OverviewRankingList
                items={pageData.rankingItems}
                isLoading={pageData.isLoading}
              />
            </Section>
          </div>

          <div className={styles["overview__row"]}>
            <PlanFeatureBlurGate
              isLocked={isSourceLocked}
              showUpgradeButton
            >
              <Section icon={<SourceUsageIcon />} title="Source Usage">
                <OverviewSourceUsage {...dataSourceUsageData} isLoading={pageData.showSourceUsageSkeleton} />
              </Section>
            </PlanFeatureBlurGate>
          </div>
        </div>
      </div>
    </>
  );
};

export default Overview;
